import { memo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CustomerDirectoryPublishGateCode } from 'pi-kiosk-shared';
import { useTenantEntitlement } from '../../features/entitlements/hooks/useTenantEntitlement.js';
import { resolveDirectoryPublishGateLabelKey } from '../../features/sales-points/domain/salesPointDirectoryEligibility.js';
import { resolveCustomerDirectoryShopOffMessageKind } from './customerDirectoryShopOffMessageKind.js';

/** Re-export shared SSOT — keeps local twin path; prevents gate-union drift. */
export type { CustomerDirectoryPublishGateCode };

export interface CustomerDirectoryTenantEligibilityViewModel {
  readonly listable: boolean;
  readonly listedAt: string | null;
  readonly hasPublishablePay: boolean;
  readonly failedPublishGates: readonly CustomerDirectoryPublishGateCode[];
  readonly stickyOos: boolean;
  readonly delistRisk: boolean;
  /** G5 — when true, pay-gate jumps use kiosks cash-box (not banks Ověřit). */
  readonly cashApproachARemediation?: boolean;
}

export type {
  CustomerDirectoryShopOffMessageKind,
} from './customerDirectoryShopOffMessageKind.js';

export interface CustomerDirectoryVisibilityCalloutProps {
  readonly eligibility: CustomerDirectoryTenantEligibilityViewModel;
  readonly onJumpToGate?: (gate: CustomerDirectoryPublishGateCode) => void;
  /**
   * When PAY_READY fails because Mobilní obchod is off (operator kill switch),
   * remap gate/copy like the per-SP strip — avoid blaming Ověřené platby alone.
   */
  readonly mobileShopOff?: boolean;
  /**
   * True only when this surface embeds an editable Mobilní obchod control
   * (e.g. SP modal commerce tab). Account never has that editor — do not pass true.
   * Omitted: never claim on-page commerce; SPI write → Open Sales points copy; else NoEditor.
   * Explicit false: always NoEditor (feature-policy) regardless of SPI.
   */
  readonly mobileShopEditorAvailable?: boolean;
  readonly className?: string;
}

export const CustomerDirectoryVisibilityCallout = memo(
  ({
    eligibility,
    onJumpToGate,
    mobileShopOff = false,
    mobileShopEditorAvailable: mobileShopEditorAvailableProp,
    className = '',
  }: CustomerDirectoryVisibilityCalloutProps): JSX.Element | null => {
    const { t } = useTranslation('admin');
    const {
      isBlockActionAllowed,
      isLoading: entitlementsLoading,
      snapshot: entitlementSnapshot,
    } = useTenantEntitlement();
    const entitlementSnapshotReady = !entitlementsLoading && entitlementSnapshot != null;
    const spiWriteEntitled =
      entitlementSnapshotReady &&
      isBlockActionAllowed('sales_point_individual_settings', 'write');
    const shopOffMessageKind = resolveCustomerDirectoryShopOffMessageKind({
      mobileShopEditorAvailableProp,
      spiWriteEntitled,
    });

    const showCallout =
      eligibility.delistRisk ||
      eligibility.stickyOos ||
      eligibility.failedPublishGates.length > 0 ||
      (!eligibility.listable && !eligibility.stickyOos);

    if (!showCallout) {
      return null;
    }

    const payReadyShopOff =
      mobileShopOff && eligibility.failedPublishGates.includes('PAY_READY');
    const gateLabelOptions = { mobileShopOff: payReadyShopOff };
    const commerceTab = String(t('kiosks.detail.tabs.commerce', 'Sales channels'));
    const salesPointsNav = String(t('kiosks.pageTitle', 'Sales points'));

    const shopsMenu = String(
      t('kiosks.directoryEligibility.customerShopsMenu', 'Shops'),
    );

    const resolveShopOffMessage = (delistRisk: boolean): string => {
      if (shopOffMessageKind === 'commerce') {
        return delistRisk
          ? t(
              'kiosks.directoryEligibility.stripDelistRiskMobileShopOffMessage',
              'Mobile shop is off and verified payments are missing. Turn on “Mobile shop” under {{commerceTab}} and restore verified payment so customers can find this place.',
              { commerceTab },
            )
          : t(
              'kiosks.directoryEligibility.stripMobileShopOffMessage',
              'Selling in the customer app is turned off for this place. Turn on “Mobile shop” under the {{commerceTab}} tab. Then finish verified payments if they are still missing.',
              { commerceTab },
            );
      }
      if (shopOffMessageKind === 'openSalesPoints') {
        return delistRisk
          ? t(
              'account.directoryVisibility.delistRiskMobileShopOffMessageOpenSalesPoints',
              'Mobile shop is off and verified payments are missing. Open {{salesPointsNav}}, turn on “Mobile shop”, and restore verified payment so customers can find this place.',
              { salesPointsNav },
            )
          : t(
              'account.directoryVisibility.mobileShopOffMessageOpenSalesPoints',
              'Selling in the customer app is turned off for this place. Open {{salesPointsNav}}, turn on “Mobile shop” there, then finish verified payments if still needed.',
              { salesPointsNav },
            );
      }
      return delistRisk
        ? t(
            'kiosks.directoryEligibility.stripDelistRiskMobileShopOffMessageNoEditor',
            'Mobile shop is off and verified payments are missing. You cannot turn mobile shop on here — ask a platform admin to allow sales-point settings. Restore verified payment so customers can find this place.',
          )
        : t(
            'kiosks.directoryEligibility.stripMobileShopOffMessageNoEditor',
            'Selling in the customer app is turned off for this place. You cannot turn it on here — ask a platform admin to allow sales-point settings for this business. Then finish verified payments if still needed.',
          );
    };

    const cutoverMassDelist =
      eligibility.delistRisk &&
      eligibility.listedAt != null &&
      eligibility.listedAt.length > 0;

    let title: string;
    let message: string;
    if (payReadyShopOff && !eligibility.delistRisk) {
      title = t(
        'kiosks.directoryEligibility.stripMobileShopOffTitle',
        'Mobile shop is off — not visible under {{shopsMenu}}',
        { shopsMenu },
      );
      message = resolveShopOffMessage(false);
    } else if (cutoverMassDelist) {
      title = t(
        'account.directoryVisibility.cutoverMassDelistTitle',
        'Meet verified-payment requirements to keep sales points in the customer app (menu item: {{shopsMenu}})',
        { shopsMenu },
      );
      if (payReadyShopOff) {
        message = resolveShopOffMessage(true);
      } else {
        message = t(
          'account.directoryVisibility.cutoverMassDelistMessage',
          'Minimum requirement for visibility under {{shopsMenu}}: working verified payments. Open Account → Payment receipt settings → Your bank accounts, save credentials for at least one rail you use, then click Verify wiring until the status is Verified so customers can find your sales points again.',
          { shopsMenu },
        );
      }
    } else if (eligibility.delistRisk) {
      title = t(
        'account.directoryVisibility.delistRiskTitle',
        'Complete verified payments so sales points appear in the customer app (menu item: {{shopsMenu}})',
        { shopsMenu },
      );
      if (payReadyShopOff) {
        message = resolveShopOffMessage(true);
      } else {
        message = t(
          'account.directoryVisibility.delistRiskMessage',
          'Minimum requirement for visibility under {{shopsMenu}}: working verified payments. Restore verified payments in this section so customers can find them. Out-of-stock sales points stay visible when payments are working.',
          { shopsMenu },
        );
      }
    } else if (eligibility.stickyOos && eligibility.listable) {
      title = t(
        'account.directoryVisibility.stickyOosTitle',
        'Listed shops stay visible when out of stock',
      );
      message = t(
        'account.directoryVisibility.stickyOosMessage',
        'Sales points that already appear in the customer app under the {{shopsMenu}} menu item stay there with an empty catalog until stock returns. They will not be removed from that menu item.',
        { shopsMenu },
      );
    } else {
      title = t(
        'account.directoryVisibility.setupTitle',
        'This sales point is not visible in the customer app',
      );
      message = t(
        'account.directoryVisibility.setupMessage',
        'To show this place to customers, finish the items below. Click each one — it opens where you can set it up.',
      );
    }

    const rootClass = ['asp-directory-visibility-callout', className]
      .filter((part) => part.length > 0)
      .join(' ');

    return (
      <section
        className={rootClass}
        role="status"
        aria-live="polite"
        data-testid="customer-directory-visibility-callout"
      >
        <div className="asp-directory-visibility-callout__head">
          <AlertTriangle
            className="h-5 w-5 shrink-0 text-[var(--color-an-warn)] stroke-[1.75]"
            aria-hidden="true"
          />
          <h2 className="asp-directory-visibility-callout__title">{title}</h2>
        </div>
        <p className="asp-directory-visibility-callout__message">{message}</p>

        {eligibility.failedPublishGates.length > 0 ? (
          <div className="asp-directory-visibility-callout__gates-block">
            <p className="asp-directory-visibility-callout__gates-heading">
              {t(
                'account.directoryVisibility.gatesHeading',
                'To show this place to customers, still missing:',
              )}
            </p>
            <ul className="asp-directory-visibility-callout__gates">
              {eligibility.failedPublishGates.map((gate) => {
                const labelKey = resolveDirectoryPublishGateLabelKey(gate, gateLabelOptions);
                const labelFallback =
                  gate === 'PAY_READY' && payReadyShopOff ? 'Mobile shop off' : gate;
                const label = t(labelKey, labelFallback);
                const content: ReactNode =
                  onJumpToGate != null ? (
                    <button
                      type="button"
                      className="asp-directory-visibility-callout__gate-jump"
                      data-testid={`customer-directory-visibility-jump-${gate}`}
                      onClick={() => onJumpToGate(gate)}
                    >
                      {label}
                    </button>
                  ) : (
                    label
                  );
                return (
                  <li key={gate} className="asp-directory-visibility-callout__gate-row">
                    {content}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </section>
    );
  },
);

CustomerDirectoryVisibilityCallout.displayName = 'CustomerDirectoryVisibilityCallout';
